<template>
  <div>
    <!-- Multiple Account Selection Modal -->
    <!-- Allow user to select one of the account for a person to start loan on -->
    <v-dialog
      persistent
      max-width="420"
      v-model="accountSelectionModalOpened"
      overlay-opacity=".95"
    >
      <v-card rounded="lg">
        <v-card-title class="justify-space-between align-start">
          <div>
            <h3 class="text-h6 font-weight-medium">
              Multiple Accounts Detected
            </h3>
            <p class="text-subtitle-2 text--secondary mt-1">
              Select the account to start the loan on.
            </p>
          </div>
        </v-card-title>
        <v-card-text class="pb-0" style="height: 250px; overflow-y: auto">
          <p class="text-weight-bold">
            Account Numbers ({{ listOfAccounts.length }})
          </p>
          <v-card
            hover
            class="px-5 py-2 mb-2 d-flex justify-space-between"
            outlined
            v-for="(a, i) in listOfAccounts"
            :key="i"
            rounded="lg"
            @click="onAccountSelected(a)"
          >
            <p class="font-weight-bold mb-0">{{ a.mainAccountNumber }}</p>
            <v-icon>mdi-chevron-right</v-icon>
          </v-card>
        </v-card-text>

        <v-card-actions class="pt-0 justify-end">
          <v-btn
            @click="accountSelectionModalOpened = false"
            min-width="120"
            min-height="42"
            depressed
            class="text-capitalize"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent max-width="520" v-model="dialog" overlay-opacity=".95">
      <v-card rounded="lg">
        <v-card-title class="justify-space-between align-start">
          <div>
            <h3 class="text-h6 font-weight-medium">Account Lookup Results</h3>
            <p class="text-subtitle-2 text--secondary mt-1">
              {{ accounts.length }} {{ resultMessage }}
            </p>
          </div>
          <v-spacer />

          <v-btn
            v-if="!integrationOptions.isIntegrated && accounts.length > 0"
            @click="openPersonModalForm"
            depressed
          >
            <v-icon>mdi-plus</v-icon>
            <span>Add Account</span>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-2 pb-0" style="height: 450px; overflow-y: auto">
          <!-- Loader -->
          <div
            style="width: 100%"
            class="d-flex justify-center text-center"
            v-if="loading"
          >
            <div>
              <v-progress-circular
                size="40"
                indeterminate
              ></v-progress-circular>
              <p class="mt-2">Searching for account...</p>
            </div>
          </div>

          <!-- New account creation button -->
          <div
            style="width: 100%"
            class="mt-12 d-flex justify-center flex-column text-center"
            v-if="
              !integrationOptions.isIntegrated && !loading && !accounts.length
            "
          >
            <v-btn class="mx-12" @click="openPersonModalForm" depressed>
              <v-icon>mdi-plus</v-icon>
              <span>Add Account</span>
            </v-btn>
            <p class="mb-0 mt-2 text-subtitle-1">Account was not found.</p>
            <p>
              Try searhing using another filter option or create the account.
            </p>
          </div>

          <!-- List of Accounts -->
          <template v-else v-for="(n, index) in accounts">
            <v-card
              :key="`searchResult-${index}`"
              rounded="lg"
              class="pb-0 shadow-lg"
            >
              <v-card-title>
                <h3
                  class="text-subtitle-1 grey--text text--darken-2 font-weight-bold"
                >
                  <span>Acc #: {{ n.account?.mainAccountNumber || '-' }}</span>
                </h3>
                <v-spacer></v-spacer>

                <v-chip
                  class="chip--poistion--right"
                  :color="
                    n.foundOn === 'core' ? 'grey darken-1' : 'grey lighten-1'
                  "
                  small
                  text-color="white"
                >
                  <v-icon size="12" class="mr-2">{{
                    n.foundOn === 'core' ? 'mdi-server' : 'mdi-database'
                  }}</v-icon>
                  {{ `${n.foundOn}` }}
                </v-chip>
              </v-card-title>
              <v-card-text class="pb-0" style="height: 125px; overflow-y: auto">
                <v-row>
                  <!-- List of Account Holders -->
                  <v-col
                    v-for="(person, index) in n.persons"
                    class="pb-0"
                    :key="index"
                  >
                    <v-card outlined rounded="lg" class="pa-2">
                      <h3
                        class="d-flex text-subtitle-2 grey--text text--darken-3 font-weight-bold mb-0"
                      >
                        {{ `${person.firstName} ${person.lastName}` }}
                        <v-spacer />
                      </h3>
                      <div
                        v-if="person.personClass"
                        class="d-flex justify-space-between align-center caption"
                      >
                        <span>Person Class</span>
                        <span>{{ person.personClass }}</span>
                      </div>
                      <div
                        class="d-flex justify-space-between align-center caption"
                      >
                        <span>Tax Number</span>
                        <span>{{ person.trn || '-' }}</span>
                      </div>
                      <div
                        class="d-flex justify-space-between align-center caption"
                      >
                        <span>Date of Birth</span>
                        <span>{{
                          person.dateOfBirth
                            ? dayjs(person.dateOfBirth).format('MMM DD, YYYY')
                            : '-'
                        }}</span>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>

              <v-card-actions class="mb-2 pt-0">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      :disabled="working && selectedIndex !== index"
                      :loading="working && selectedIndex === index"
                      @click="openPersonModalForm(index, n)"
                      v-bind="attrs"
                      v-on="on"
                      depressed
                      small
                      min-height="36"
                      class="text-capitalize ml-3"
                      color="primary"
                    >
                      Select Account
                      <v-icon left>mdi-arrow-right</v-icon>
                    </v-btn>
                  </template>
                  <span>Use this account to start the process</span>
                </v-tooltip>
              </v-card-actions>
            </v-card>
          </template>
        </v-card-text>

        <v-card-actions class="pt-0 justify-end">
          <v-btn
            @click="closeModal()"
            min-width="120"
            min-height="42"
            depressed
            class="text-capitalize"
            >Cancel</v-btn
          >
          <v-btn
            @click="goBack()"
            min-width="120"
            min-height="42"
            depressed
            color="primary"
            class="text-capitalize"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Back To Search
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loan Application  -->
    <LoanApplication
      ref="loanApplication"
      @closed="$refs.accountForm.closeModal()"
    />

    <!-- Person Condensed Form Details  -->
    <AccountForm ref="accountForm" @accountSaved="onAccountSaved" />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import PageMixin from '@/mixins/page.mixin';
import LoanApplication from '@/modules/loanpro/components/loan-pro/WorkLoad/LoanApplication.vue';
import AccountForm from '@/modules/loanpro/components/loan-pro/Search/AccountForm.vue';
import AccountManagementService from '@/modules/loanpro/services/account-management.service';
import { CoreIntegrationService } from '@/modules/integrations/services/core/core-integration.service';

const accountManagementService = new AccountManagementService();

export default {
  components: { LoanApplication, AccountForm },
  mixins: [PageMixin],
  data() {
    return {
      dayjs: dayjs,
      dialog: false,
      loading: false,
      searchDetails: {},
      accounts: [],
      working: false,
      selectedIndex: -1,
      integrationOptions: {
        isIntegrated: false,
        core: ''
      },

      listOfAccounts: [],
      accountSelectionModalOpened: false,
      accountDetails: { account: null, persons: [] }
    };
  },

  computed: {
    resultMessage: function () {
      return this.accounts.length > 1 ? 'results found' : 'result found';
    }
  },

  async mounted() {
    const coreIntegrationService = new CoreIntegrationService();
    this.integrationOptions =
      await coreIntegrationService.getCoreIntegratedStatus();
  },

  methods: {
    async onAccountSaved(accountDetails) {
      await this.$refs.loanApplication.newApplication(accountDetails);
      this.closeModal();
    },

    async onAccountSelected(account) {
      this.accountSelectionModalOpened = false;

      this.accountDetails.account = account;

      //Open account form to edit account details based on the selected account
      this.$refs.accountForm.openModal(
        this.accountDetails,
        this.integrationOptions
      );
    },

    async openPersonModalForm(selectedIndex, n) {
      try {
        this.accountSelectionModalOpened = false;
        this.selectedIndex = selectedIndex;
        this.working = true;

        if (n) {
          //Get account details based on the account id
          const coreIntegrationService = new CoreIntegrationService();
          const { account, persons, hasMultipleAccounts, accounts } =
            await coreIntegrationService.findAccountDetails({
              account: n.account,
              persons: n.persons
            });

          this.accountDetails = { account, persons };

          //Open account selection dialog if person has multiple account.
          if (hasMultipleAccounts) {
            this.dialog = false;
            this.listOfAccounts = accounts;
            this.accountSelectionModalOpened = true;
            return;
          }
        }

        //Open account form to edit account details
        this.$refs.accountForm.openModal(
          this.accountDetails,
          this.integrationOptions
        );
      } catch (e) {
        console.log(e);
        this.showSnack({
          color: 'error',
          title: `Something went wrong in carrying out this action, please try again.`,
          message: ``
        });
      } finally {
        this.working = false;
        this.selectedIndex = -1;
      }
    },

    async closeModal() {
      this.dialog = false;
    },

    async search() {
      try {
        this.loading = true;

        const coreIntegrationService = new CoreIntegrationService();
        this.accounts = await coreIntegrationService.findPersons(
          this.searchDetails
        );
      } catch (e) {
        console.log(e);
        this.showSnack({
          color: 'error',
          title: `Something went wrong in carrying out this action, please try searching again.`,
          message: ``
        });
      } finally {
        this.loading = false;
      }
    },

    async openModal(searchParams) {
      this.dialog = true;
      this.searchDetails = { ...searchParams };
      this.search();
    },

    async goBack() {
      this.$emit('goBack', this.searchDetails);
      this.closeModal();
    }
  }
};
</script>
